import type React from 'react';
import { useEffect, useRef, useLayoutEffect } from 'react';

type UseAudioEventListenerProps = {
	audioRef: React.RefObject<HTMLAudioElement>;
	onContentPaused: () => void;
	onContentResumed: () => void;
	onContentEnded: () => void;
	onContentCanPlay: () => void;
	onContentBuffer?: () => void;
};

type CallbackProps = Omit<UseAudioEventListenerProps, 'audioRef'>;

export const useAudioEventListener = ({
	audioRef,
	onContentPaused,
	onContentResumed,
	onContentEnded,
	onContentCanPlay,
	onContentBuffer,
}: UseAudioEventListenerProps) => {
	const savedCallbacks = useRef<CallbackProps>({
		onContentPaused,
		onContentResumed,
		onContentEnded,
		onContentCanPlay,
		onContentBuffer,
	});

	useLayoutEffect(() => {
		savedCallbacks.current = {
			onContentPaused,
			onContentResumed,
			onContentEnded,
			onContentCanPlay,
			onContentBuffer,
		};
	}, [onContentPaused, onContentResumed, onContentEnded, onContentCanPlay, onContentBuffer]);

	useEffect(() => {
		const audio = audioRef.current;
		if (!audio) {
			return;
		}

		const onPaused = () => savedCallbacks.current.onContentPaused();
		const onPlayed = () => savedCallbacks.current.onContentResumed();
		const onEnded = () => savedCallbacks.current.onContentEnded();
		const onCanPlay = () => savedCallbacks.current.onContentCanPlay();
		const onBuffer = () => savedCallbacks.current.onContentBuffer?.();

		audio.addEventListener('pause', onPaused);
		audio.addEventListener('play', onPlayed);
		audio.addEventListener('ended', onEnded);
		audio.addEventListener('canplay', onCanPlay);
		audio.addEventListener('waiting', onBuffer);

		return () => {
			audio.removeEventListener('pause', onPaused);
			audio.removeEventListener('play', onPlayed);
			audio.removeEventListener('ended', onEnded);
			audio.removeEventListener('canplay', onCanPlay);
			audio.removeEventListener('waiting', onBuffer);
		};
	}, [audioRef]);
};
